import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import {
  COST_BREAKDOWNS,
  type CostBreakdown,
} from 'lumen/utils/net-revenue/cost-breakdowns';

interface NetRevenueRowPayload {
  id?: string | null;
}

interface NetRevenueBreakdownRowPayload extends NetRevenueRowPayload {
  is_unmapped?: boolean;
  name: string | null;
  parent_id?: string | null;
}

interface NetRevenueStayDatePayload extends NetRevenueRowPayload {
  date: string;
}

interface NetRevenueDashboardPayload {
  available_breakdowns?: CostBreakdown[];
  channel?: NetRevenueBreakdownRowPayload[];
  cost_profiles?: NetRevenueBreakdownRowPayload[];
  id: string;
  market_segment?: NetRevenueBreakdownRowPayload[];
  stay_date?: NetRevenueStayDatePayload[];
  total?: NetRevenueRowPayload;
}

// Reads the raw ids, so call it before they get their prefix.
function isUnmappedPayload(row: NetRevenueBreakdownRowPayload): boolean {
  return row.id == null && row.parent_id == null && row.name == null;
}

// Breakdowns share one model, so ids are prefixed to keep records apart.
// Every row has an id except the unmapped one. Parent ids get the same
// prefix so a child's `parentId` still matches its parent's `id`.
function assignBreakdownIds(
  rows: NetRevenueBreakdownRowPayload[] | undefined,
  prefix: string,
): void {
  rows?.forEach((row) => {
    row.is_unmapped = isUnmappedPayload(row);
    row.id = `${prefix}:${row.id ?? 'unmapped'}`;
    if (row.parent_id != null) {
      row.parent_id = `${prefix}:${row.parent_id}`;
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class NetRevenueDashboardSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    total: { embedded: 'always' },
    channel: { embedded: 'always' },
    costProfiles: { embedded: 'always' },
    marketSegment: { embedded: 'always' },
    stayDate: { embedded: 'always' },
  };

  modelNameFromPayloadKey(key: string): string {
    if (key === 'net_revenue_periods') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return super.modelNameFromPayloadKey('net-revenue/dashboard');
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return super.modelNameFromPayloadKey(key);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  normalize(typeClass: ModelSchema, resourceHash: NetRevenueDashboardPayload) {
    const periodId = resourceHash.id;

    if (resourceHash.total) {
      resourceHash.total.id = `${periodId}:total`;
    }

    assignBreakdownIds(resourceHash.cost_profiles, `${periodId}:cost-profile`);
    assignBreakdownIds(resourceHash.channel, `${periodId}:channel`);
    assignBreakdownIds(
      resourceHash.market_segment,
      `${periodId}:market-segment`,
    );

    // An empty hasMany can't tell "not served yet" from "no rows".
    resourceHash.available_breakdowns = [
      ...(resourceHash.channel ? [COST_BREAKDOWNS.CHANNEL] : []),
      ...(resourceHash.market_segment ? [COST_BREAKDOWNS.MARKET_SEGMENT] : []),
      COST_BREAKDOWNS.COST_PROFILE,
    ];

    resourceHash.stay_date?.forEach((point) => {
      point.id = `${periodId}:stay-date:${point.date}`;
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return super.normalize(typeClass, resourceHash);
  }
}
